import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import styled from "styled-components";
import { CurrencyCodes } from "@shared_packages/toolkit/types";
import {
  AmountCell,
  Button,
  ButtonVariant,
  DateTableCell,
  ReactTableComponent,
  Status,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import { useAuthUserContext } from "../../../../context/AdministratorContextProvider";
import {
  WALLET_FEE_ADMIN_PROVIDERS,
  WALLET_PROVIDER_FIRE,
  walletFeeAdminProviderLabel,
} from "../../billing/components/walletFeeFormConstants";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 20px;
`;

const Label = styled(Typography)`
  color: #6b7280;
`;

const ProviderSelect = styled.select`
  min-width: 220px;
  height: 36px;
  border-radius: 5px;
  border: 1px solid #dbe3eb;
  padding: 0 12px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: #0e233e;
  background: #fff;
  outline: none;
  &:focus {
    border-color: #027aff;
    box-shadow: 0 0 0 3px rgba(2, 120, 255, 0.153);
  }
`;

type WalletPayoutRow = NonNullable<
  Awaited<ReturnType<typeof api.getCompanyWalletPayouts>>["docs"]
>[number];

function isRetryableFeeChargeStatus(status: string | undefined): boolean {
  const s = String(status ?? "").toLowerCase();
  return s === "failed" || s === "insufficient_funds";
}

export const CompanyWalletPayoutsTab: React.FC<{ companyId: string }> = ({
  companyId,
}) => {
  const { showNotification } = useNotification();
  const { authUser } = useAuthUserContext();
  const canRetry = !!authUser?.policies?.actionsOnWalletFeeSettings;
  const [selectedProvider, setSelectedProvider] =
    useState<string>(WALLET_PROVIDER_FIRE);
  const [connectedProviders, setConnectedProviders] = useState<string[]>([]);
  const [rows, setRows] = useState<WalletPayoutRow[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const providerOptions = useMemo(() => {
    if (connectedProviders.length > 0) {
      return connectedProviders.map((provider) => ({
        value: provider,
        label: walletFeeAdminProviderLabel(provider),
      }));
    }
    return WALLET_FEE_ADMIN_PROVIDERS;
  }, [connectedProviders]);

  const loadPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await api.getCompanyWalletPayouts(companyId, {
        walletProvider: selectedProvider,
        page,
        limit,
      });
      setRows(payload.docs ?? []);
      setTotalDocs(payload.totalDocs ?? 0);
      setConnectedProviders(payload.connectedProviders ?? []);
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "Could not load wallet payouts"),
        NotificationTypes.WARNING
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedProvider, page, limit, showNotification]);

  useEffect(() => {
    void loadPayouts();
  }, [loadPayouts]);

  useEffect(() => {
    if (
      connectedProviders.length > 0 &&
      !connectedProviders.includes(selectedProvider)
    ) {
      setSelectedProvider(connectedProviders[0]);
      setPage(1);
    }
  }, [connectedProviders, selectedProvider]);

  const onRetry = useCallback(
    async (row: WalletPayoutRow) => {
      const payoutId = String(row._id ?? "");
      if (!payoutId) return;
      setRetryingId(payoutId);
      try {
        await api.retryCompanyWalletFeeChargePayout(companyId, payoutId);
        showNotification(
          "Fee charge retry submitted",
          NotificationTypes.SUCCESS
        );
        await loadPayouts();
      } catch (e: unknown) {
        showNotification(
          getApiErrorMessage(e, "Could not retry fee charge"),
          NotificationTypes.DANGER
        );
      } finally {
        setRetryingId(null);
      }
    },
    [companyId, loadPayouts, showNotification]
  );

  const columns = useMemo<Array<ColumnDef<WalletPayoutRow, any>>>(
    () => [
      {
        header: "Amount",
        accessorKey: "amount",
        cell: ({ row: { original } }) => (
          <AmountCell
            currency={
              (original.currency as CurrencyCodes) ??
              CurrencyCodes.POUND_STERLING
            }
            amount={original.amount ?? "0.00"}
            direction="outgoing"
          />
        ),
      },
      {
        header: "Status",
        accessorKey: "chainStatus",
        cell: ({ row: { original } }) => (
          <Status status={original.chainStatus ?? original.status} />
        ),
      },
      {
        header: "Created",
        accessorKey: "createdAt",
        cell: ({ getValue }) =>
          getValue() ? <DateTableCell date={getValue()} /> : "—",
      },
      {
        header: "Reference",
        accessorKey: "reference",
        cell: ({ getValue }) => getValue() ?? "—",
      },
      ...(canRetry
        ? [
            {
              header: "Actions",
              id: "actions",
              cell: ({
                row: { original },
              }: {
                row: { original: WalletPayoutRow };
              }) => {
                const status = original.chainStatus ?? original.status;
                if (!isRetryableFeeChargeStatus(status)) {
                  return "—";
                }
                const payoutId = String(original._id ?? "");
                const busy = retryingId === payoutId;
                return (
                  <Button
                    variant={ButtonVariant.OUTLINE}
                    type="button"
                    disabled={busy || loading}
                    onClick={() => void onRetry(original)}
                  >
                    {busy ? "Retrying..." : "Retry"}
                  </Button>
                );
              },
            } as ColumnDef<WalletPayoutRow, any>,
          ]
        : []),
    ],
    [canRetry, loading, onRetry, retryingId]
  );

  return (
    <Wrap>
      <Controls>
        <Label variant={TypographyVariant.BODY7}>Wallet provider</Label>
        <ProviderSelect
          value={selectedProvider}
          onChange={(e) => {
            setSelectedProvider(e.target.value);
            setPage(1);
          }}
        >
          {providerOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </ProviderSelect>
      </Controls>

      {loading && rows.length === 0 ? (
        <Typography variant={TypographyVariant.BODY1}>
          Loading wallet payouts...
        </Typography>
      ) : (
        <ReactTableComponent
          columns={columns}
          data={rows}
          withPagination={{
            totalDocs,
            page,
            limit,
            onPaginationChange: (nextPage, nextLimit) => {
              setPage(nextPage);
              setLimit(nextLimit);
            },
          }}
        />
      )}
    </Wrap>
  );
};
